import { Box, Button, FormikTextareaInput, Modal, toast } from '@palmetto/palmetto-components';
import { Field, Form, Formik } from 'formik';

import { EntityType, QueueRejectionReason, QueueStatus, QueueType, ReferenceIdObject } from 'types';

import { useQueueClaimConflict } from '../../hooks/useQueueClaimConflict';
import { useGetQueueByAccountIdAndTypeQuery, useUpdateQueueMutation } from '../../services/queues';

interface WelcomeCallReviewModalProps {
  accountId: string;
  decision: 'approve' | 'reject';
  onClose: () => void;
}

const WelcomeCallReviewModal = ({ accountId, decision, onClose }: WelcomeCallReviewModalProps) => {
  const { data: queue } = useGetQueueByAccountIdAndTypeQuery({ accountId, type: QueueType.welcomeCall });
  const [updateQueue] = useUpdateQueueMutation();
  const { checkClaimAndProceed, claimConflictModal } = useQueueClaimConflict(queue);

  const isApprove = decision === 'approve';
  const title = isApprove ? 'Approve Welcome Call' : 'Fail Welcome Call';

  const performSubmit = async (values: { reason: string }, { setSubmitting }: any) => {
    if (!queue?.id) {
      toast.error('No open welcome call queue found.');
      setSubmitting(false);
      return;
    }
    const documentId = queue?.referenceIds?.find((ref: ReferenceIdObject) => ref.type === EntityType.documents)?.id;

    try {
      await updateQueue({
        id: queue.id,
        data: isApprove
          ? { status: QueueStatus.approved, approvalNote: values.reason.trim() }
          : {
              status: QueueStatus.rejected,
              rejectionReasons: [
                {
                  reasons: [QueueRejectionReason.other],
                  note: values.reason.trim(),
                  ...(documentId && { rejectedDocumentId: documentId }),
                },
              ],
            },
      }).unwrap();
      toast.success(isApprove ? 'Welcome call approved.' : 'Welcome call failed.');
      onClose();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error submitting the welcome call review.');
    }
    setSubmitting(false);
  };

  const handleSubmit = (...args: Parameters<typeof performSubmit>) => {
    checkClaimAndProceed(() => performSubmit(...args));
  };

  return (
    <Modal
      isOpen
      onDismiss={onClose}
      maxWidth="4xl"
      ariaLabelledBy="welcomeCallReviewHeader"
      fullScreenMobile
      overflow="scroll"
    >
      <Modal.Header id="welcomeCallReviewHeader" title={title} onDismiss={onClose} />
      {claimConflictModal}
      <Formik initialValues={{ reason: '' }} onSubmit={handleSubmit}>
        {({ isSubmitting, values }) => (
          <Form noValidate id="welcomeCallReviewForm">
            <Modal.Body background="secondary" gap="md" fontSize="sm">
              <Box gap="md">
                <Field
                  name="reason"
                  id="reason"
                  component={FormikTextareaInput}
                  autoComplete="off"
                  isDisabled={isSubmitting}
                  placeholder={
                    isApprove
                      ? 'Explain why the welcome call is being approved...'
                      : 'Explain why the welcome call is being failed...'
                  }
                />
              </Box>
            </Modal.Body>
            <Modal.Footer>
              <Button
                type="button"
                variant="secondary"
                tone="neutral"
                size="md"
                onClick={onClose}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                size="md"
                tone={isApprove ? 'primary' : 'danger'}
                type="submit"
                isLoading={isSubmitting}
                isDisabled={!values.reason.trim()}
              >
                {isApprove ? 'Approve' : 'Fail'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default WelcomeCallReviewModal;
